import React from 'react';
import { useIntl } from 'react-intl';

import { apolloHooks } from '@nxte-nl/apollo-hooks';

import {
  RedeemCheckoutPaymentPresentational,
} from '../../components/RedeemCheckout/RedeemCheckout';
import { redeemTabWarningMessages } from '../../constants/redeemTabWarning';
import { redeemOrder as redeemOrderQuery } from '../../queries/redeemOrder.gql';
import {
  GlobalMessages,
  RedeemCartMessages,
  RedeemMessages,
  RedeemSignals,
} from '../../types/Messages';
import {
  RedeemOrder,
  Currency,
  RedeemPackage,
  Price,
  RedeemShipment,
  GiftcardPackage,
  GiftcardShipment,
  GiftcardOrderItem,
  GiftCardVariationType,
  GiftCardData,
} from '../../types/apiTypes';
import {
  PricePresentational,
  RedeemGuestModalType,
} from '../../types/common';
import { beforeUnloadManager } from '../../utils/beforeUnloadManager/beforeUnloadManager';
import { redeemUtils } from '../../utils/redeemUtils/redeemUtils';
import { messageHooks } from '../messageHooks/messageHooks';

export type RedeemOrderResponse = {
  data: {
    order: RedeemOrder | null;
  };
};

export const redeemHooks = {
  useGetState() {
    const [response, setResponse] = React.useState<{ data: RedeemOrder | null }>();
    return { response, setResponse };
  },
  useCalculatePriceToPay(
    payments: RedeemCheckoutPaymentPresentational[] | null,
    grandTotal: PricePresentational | null,
  ) {
    return React.useMemo(() => {
      const totalPaymentsSum = payments?.reduce(
        (previousValue, currentValue) => previousValue + currentValue.value.value,
        0,
      ) ?? 0;
      const totalPayments = {
        currency: payments?.[0].value.currency || Currency.EUR,
        value: totalPaymentsSum,
      };

      if (grandTotal !== null) {
        const val = grandTotal.value - totalPaymentsSum;

        if (val > 0) {
          return {
            priceToPay: {
              currency: grandTotal.currency,
              value: +val.toFixed(2),
            } as Price,
            totalPayments: totalPayments,
          };
        }
      }

      return {
        priceToPay: {
          currency: payments?.[0].value.currency || Currency.EUR,
          value: 0,
        } as Price,
        totalPayments: totalPayments,
      };
    }, [grandTotal, payments]);
  },

  useRedeemOrder(uuid: string | undefined, enabled: boolean) {
    const { response, setResponse } = redeemHooks.useGetState();

    const { loading } = apolloHooks.useQuery(
      redeemOrderQuery,
      {
        variables: React.useMemo(() => ({ uuid }), [uuid]),
        onCompleted: setResponse,
        skip: !enabled,
      },
    );

    return {
      loading,
      redeemData: response?.data,
    };
  },

  useCalculateTotalCadeaubon(packages: RedeemPackage[] | null): Price | null {
    return React.useMemo(() => {
      const sum = packages?.reduce(
        (previousValue, currentValue) =>
          previousValue + currentValue.packageTotals.mainItemAmount.value,
        0,
      );

      return packages?.length
        ? ({
          currency: packages[0].packageTotals.mainItemAmount.currency || Currency.EUR,
          value: sum,
        } as Price)
        : null;
    }, [packages]);
  },

  useResetRedeemData() {
    const setRedeemToken = redeemHooks.useSetRedeemToken();
    const setRedeemCardData = redeemHooks.useSetRedeemCardData();
    const setRedeemData = messageHooks.useNextWithValue(RedeemMessages.RedeemData, null);
    const setDoublerModalWasOpenedDuringThisSession = messageHooks.useNextWithValue(
      RedeemMessages.RedeemDoublerModalOpened,
      false,
    );
    const setShouldAddDigitalAddress =
      messageHooks.useNextWithValue(RedeemMessages.ShouldAddDigitalAddress, false);
    const setShouldAddPhysicalAddress =
      messageHooks.useNextWithValue(RedeemMessages.ShouldAddPhysicalAddress, false);

    return React.useCallback(() => {
      setRedeemToken(null);
      setRedeemData();
      setRedeemCardData(null);
      setDoublerModalWasOpenedDuringThisSession();
      setShouldAddDigitalAddress();
      setShouldAddPhysicalAddress();
    }, [
      setRedeemCardData,
      setRedeemData,
      setRedeemToken,
      setDoublerModalWasOpenedDuringThisSession,
      setShouldAddDigitalAddress,
      setShouldAddPhysicalAddress,
    ]);
  },

  usePackagesToOneArray() {
    return React.useCallback(
      (
        shipments?:
          | (GiftcardShipment | RedeemShipment)[]
          | ((GiftcardShipment | RedeemShipment)[] & RedeemShipment[]),
      ) => {
        return shipments?.length
          ? shipments.reduce(
            (
              packages: (RedeemPackage | GiftcardPackage)[],
              shipment: RedeemShipment | GiftcardShipment,
            ) => [...packages, ...shipment.packages] as (RedeemPackage | GiftcardPackage)[],
            [],
          )
          : null;
      },
      [],
    );
  },

  useRedeemGuestDeliveryModal() {
    const onRedeemGuestOrderModalOpen =
      messageHooks.useNextSignal(RedeemSignals.onRedeemGuestOrderModalOpen);

    const shouldAddDigitalAddress =
      messageHooks.useSubjectState(RedeemMessages.ShouldAddDigitalAddress);
    const shouldAddPhysicalAddress =
      messageHooks.useSubjectState(RedeemMessages.ShouldAddPhysicalAddress);

    const handleOpenGuestDeliveryModal = React.useCallback(() => {
      if (shouldAddPhysicalAddress) {
        onRedeemGuestOrderModalOpen(RedeemGuestModalType.Physical);
        return;
      }

      if (shouldAddDigitalAddress) {
        onRedeemGuestOrderModalOpen(RedeemGuestModalType.Digital);
      }
    }, [onRedeemGuestOrderModalOpen, shouldAddDigitalAddress, shouldAddPhysicalAddress]);

    return {
      handleOpenGuestDeliveryModal,
    };
  },

  useSetShouldAddAddresses() {
    const setShouldAddDigitalAddress =
      messageHooks.useNext(RedeemMessages.ShouldAddDigitalAddress);
    const setShouldAddPhysicalAddress =
      messageHooks.useNext(RedeemMessages.ShouldAddPhysicalAddress);

    return React.useCallback((packages: RedeemPackage[]) => {
      let shouldAddDigitalAddress = false;
      let shouldAddPhysicalAddress = false;

      if (packages !== null && packages.length > 0) {
        packages.forEach((orderItem) => {
          orderItem.items.forEach((item) => {
            if (item.__typename === 'GiftcardOrderItem') {
              if ((item as GiftcardOrderItem).variation.variationType
                === GiftCardVariationType.DIGITAL) {
                shouldAddDigitalAddress = true;
              }

              if ((item as GiftcardOrderItem).variation.variationType
                === GiftCardVariationType.PHYSICAL) {
                shouldAddPhysicalAddress = true;
              }
            }

            if (item.__typename === 'DonationOrderItem') {
              shouldAddDigitalAddress = true;
            }
          });
        });
      }

      setShouldAddDigitalAddress(shouldAddDigitalAddress);
      setShouldAddPhysicalAddress(shouldAddPhysicalAddress);
    }, [setShouldAddDigitalAddress, setShouldAddPhysicalAddress]);
  },

  useUpdateRedeemCartMessages() {
    const setRedeemPackages = messageHooks.useNext(RedeemCartMessages.RedeemCartPackages);
    const setRedeemCartData = messageHooks.useNext(RedeemCartMessages.RedeemCart);

    const packagesToArray = redeemHooks.usePackagesToOneArray();
    const handleShouldUpdateAddresses = redeemHooks.useSetShouldAddAddresses();

    return React.useCallback(
      (response: RedeemOrderResponse) => {
        const packages = packagesToArray(response.data.order?.shipments) as RedeemPackage[];

        setRedeemPackages(packages);
        handleShouldUpdateAddresses(packages);

        setRedeemCartData(response.data.order);
      },
      [
        packagesToArray,
        setRedeemPackages,
        setRedeemCartData,
        handleShouldUpdateAddresses,
      ],
    );
  },
  useGetRedeemToken() {
    const redeemToken = messageHooks.useSubjectState(GlobalMessages.RedeemToken);
    return React.useMemo(() => {
      return redeemToken ? redeemToken : redeemUtils.getRedeemToken();
    }, [redeemToken]);
  },
  useSetRedeemToken() {
    const setToken = messageHooks.useNext(GlobalMessages.RedeemToken);
    const setStorageToken = redeemUtils.setRedeemToken;
    return React.useCallback((token: string | null) => {
      setToken(token);
      setStorageToken(token);
    }, [setToken, setStorageToken]);
  },
  useGetRedeemCardData() {
    const redeemCardData = messageHooks.useSubjectState(RedeemMessages.RedeemCardData);
    return React.useMemo(() => {
      return redeemCardData ? redeemCardData : redeemUtils.getRedeemCardData();
    }, [redeemCardData]);
  },
  useSetRedeemCardData() {
    const setRedeemCardData = messageHooks.useNext(RedeemMessages.RedeemCardData);
    const setStorageCardData = redeemUtils.setRedeemCardData;
    return React.useCallback((data: GiftCardData | null) => {
      setRedeemCardData(data);
      setStorageCardData(data);
    }, [setRedeemCardData, setStorageCardData]);
  },
  useShowWarningBeforeClosingBrowserTab() {
    // This warning will not be displayed in modern browsers, but something is required
    // to be returned and current default message from Chrome seems like a way to go.
    const intl = useIntl();
    const warning = intl.formatMessage(redeemTabWarningMessages.warningBeforeClosingBrowserTab);
    const redeemToken = messageHooks.useSubjectState(GlobalMessages.RedeemToken);

    React.useEffect(() => {
      beforeUnloadManager.detach();

      if (redeemToken !== null) {
        beforeUnloadManager.setMessage(warning);
        beforeUnloadManager.attach();
      }

      return () => {
        beforeUnloadManager.detach();
      };
    }, [redeemToken, warning]);

    return {
      removeClosingTabListener: beforeUnloadManager.detach,
    };
  },
};
